function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import has from 'lodash/has';
import { call, all, put, select, take, takeLatest, getContext } from 'redux-saga/effects';
import includes from 'lodash/includes';
import get from 'lodash/get';
import { LocalError } from 'helpers/errorTypes';
import LocalStorage from 'libs/LocalStorage';
import { getKeyFromPem, symmetricDecrypt } from 'helpers/crypto';
import { decryptEncodedEncryptedHolderPassphrase } from 'helpers/wallet';
import { clearHashCookie } from 'helpers/integration';
import ApiService from 'services/ApiService';
import App from 'modules/App';
import Account from 'modules/Account';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import * as selectors from './selectors';
import messages from './messages';
// INTEGRATION ---------------------------------------------------------------------------------------------------------
function* fetchAccount() {
    try {
        const account = yield call(ApiService.regionalRequest, '/api/Integration/Account', {
            credentials: 'include'
        });
        yield put(App.actions.setRegion(account.countryCode));
        yield put(actions.fetchAccountSuccess(account));
    } catch (err) {
        yield call(App.dispatchError, err, messages);
        yield put(actions.fetchAccountError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchClient({ payload }) {
    try {
        const { clientId } = payload;
        const client = yield call(ApiService.regionalRequest, `/api/Integration/Client/${clientId}`, {
            credentials: 'include'
        });
        yield put(actions.fetchClientSuccess(client));
    } catch (err) {
        yield call(App.dispatchError, err, messages);
        yield put(actions.fetchClientError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* initialize({ payload }) {
    try {
        const { clientId, scopes, returnUrl } = payload;
        yield all([
            put(actions.fetchClient(clientId)),
            put(actions.fetchAccount())
        ]);
        yield all([
            take([
                actionTypes.FETCH_CLIENT_SUCCESS,
                actionTypes.FETCH_CLIENT_ERROR
            ]),
            take([
                actionTypes.FETCH_ACCOUNT_SUCCESS,
                actionTypes.FETCH_ACCOUNT_ERROR
            ])
        ]);
        const accountCountryCode = yield select(selectors.accountCountryCode);
        const clientAllowedCountryCodes = yield select(selectors.clientAllowedCountryCodes);
        if (!clientAllowedCountryCodes || clientAllowedCountryCodes.length && !includes(clientAllowedCountryCodes, accountCountryCode)) {
            const country = yield select(App.selectors.countryByAlpha2Code(accountCountryCode));
            const countryNameKey = get(country, 'nameKey');
            const countryLocalizationResource = yield select(App.selectors.localizationResourceByResourceKey(countryNameKey));
            throw new LocalError({
                code: 'ClientCountryIsNotAllowedForIntegration',
                params: {
                    country: get(countryLocalizationResource, 'value', accountCountryCode)
                }
            });
        }
        const requireConsent = yield select(App.selectors.requireConsent);
        if (!requireConsent) {
            yield put(actions.acceptConsents(scopes, returnUrl));
            take([
                actionTypes.ACCEPT_CONSENTS_SUCCESS,
                actionTypes.ACCEPT_CONSENTS_ERROR
            ]);
        }
        yield put(actions.initializeSuccess());
    } catch (err) {
        yield call(App.dispatchError, err, messages);
        yield put(actions.initializeError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* resolveAccount() {
    let account = yield select(selectors.account);
    if (!account) {
        account = yield call(fetchAccount);
    }
    return account;
}
function* resolvePassphrase(account) {
    let passphrase = yield select(Account.selectors.passphrase);
    if (!passphrase) {
        const cookies = yield getContext('cookies');
        const pcHash = cookies.get('hash');
        passphrase = yield call(symmetricDecrypt, account.encryptedPassphrase, pcHash);
    }
    return passphrase;
}
function* resolveHolderEntry() {
    const account = yield call(resolveAccount);
    const passphrase = yield call(resolvePassphrase, account);
    const privateKey = yield call(getKeyFromPem, account.encryptedPrivateKey, passphrase);
    if (!has(account, 'encryptedHolderPassphrase') || !has(account, 'holderUID')) {
        return null;
    }
    if (!privateKey) {
        return null;
    }
    return {
        holderUID: account.holderUID,
        holderPassphrase: decryptEncodedEncryptedHolderPassphrase(account.encryptedHolderPassphrase, privateKey)
    };
}
//----------------------------------------------------------------------------------------------------------------------
function* acceptConsents({ payload }) {
    try {
        const requestUrl = '/api/Integration/ApproveConsents';
        const holderEntry = yield call(resolveHolderEntry);
        if (!holderEntry) {
            const err = new LocalError({
                code: 'NoHolderEntry'
            });
            yield put(actions.acceptConsentsError(err));
            yield call(App.dispatchError, err, messages);
            return;
        }
        const { redirectUrl } = yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            credentials: 'include',
            body: _object_spread({
                holderEntry
            }, payload)
        });
        yield put(actions.acceptConsentsSuccess());
        yield call(clearHashCookie);
        const externalRedirect = yield getContext('externalRedirect');
        const isMobileDisplayMode = yield select(App.selectors.isMobileDisplayMode);
        if (isMobileDisplayMode) {
            const domain = yield getContext('domain');
            const externalReturnUrl = encodeURIComponent(redirectUrl);
            LocalStorage.setItem('mobileSetupExternalReturnUrl', externalReturnUrl);
            externalRedirect(`${domain}/mobile/setup`);
        } else {
            externalRedirect(redirectUrl);
        }
    } catch (err) {
        yield put(actions.acceptConsentsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* rejectConsents({ payload }) {
    try {
        const requestUrl = '/api/Integration/RejectConsents';
        const { redirectUrl } = yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            credentials: 'include',
            body: payload
        });
        yield put(actions.rejectConsentsSuccess());
        yield call(clearHashCookie);
        const externalRedirect = yield getContext('externalRedirect');
        externalRedirect(redirectUrl);
    } catch (err) {
        yield call(App.dispatchError, err, messages);
        yield put(actions.rejectConsentsError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* sagas() {
    yield takeLatest(actionTypes.INITIALIZE, initialize);
    yield takeLatest(actionTypes.FETCH_ACCOUNT, fetchAccount);
    yield takeLatest(actionTypes.FETCH_CLIENT, fetchClient);
    yield takeLatest(actionTypes.ACCEPT_CONSENTS, acceptConsents);
    yield takeLatest(actionTypes.REJECT_CONSENTS, rejectConsents);
}
export default [
    sagas
];
